import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import MarketingLayout from './MarketingLayout';

const CSS = `
  .pp-hero { padding: 72px 24px 56px; background: linear-gradient(180deg, #f0f7ff 0%, #ffffff 100%); text-align: center; }
  .pp-hero-inner { max-width: 820px; margin: 0 auto; }
  .pp-badge { display: inline-flex; align-items: center; gap: 6px; padding: 5px 14px; border-radius: 999px; background: #fff; border: 1px solid #c7deef; font-size: 13px; font-weight: 500; color: #0f172a; margin-bottom: 20px; }
  .pp-h1 { font-size: 48px; font-weight: 700; letter-spacing: -0.025em; line-height: 1.1; margin: 0 0 18px; color: #0f172a; }
  .pp-h1 .accent { color: #0969da; }
  .pp-sub { font-size: 17px; color: #475569; line-height: 1.6; max-width: 680px; margin: 0 auto 28px; }
  .pp-cta-row { display: inline-flex; gap: 12px; flex-wrap: wrap; justify-content: center; }
  .pp-btn { display: inline-flex; align-items: center; justify-content: center; padding: 11px 24px; border-radius: 999px; font-size: 15px; font-weight: 500; text-decoration: none; border: none; cursor: pointer; transition: background .15s, transform .15s; }
  .pp-btn-primary { background: #0969da; color: #fff; }
  .pp-btn-primary:hover { background: #085bbf; transform: translateY(-1px); }
  .pp-btn-outline { background: #fff; border: 1px solid #cbd5e1; color: #0f172a; }
  .pp-btn-outline:hover { border-color: #0969da; color: #0969da; }

  .pp-section { max-width: 1120px; margin: 0 auto; padding: 56px 24px; }
  .pp-section-h { font-size: 32px; font-weight: 700; letter-spacing: -0.02em; text-align: center; line-height: 1.2; margin: 0 0 14px; }
  .pp-section-sub { text-align: center; max-width: 640px; margin: 0 auto 36px; color: #475569; font-size: 15px; line-height: 1.6; }

  .pp-feat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  .pp-feat { padding: 24px; border: 1px solid #e5e7eb; border-radius: 16px; background: #fff; transition: transform .2s, box-shadow .2s; }
  .pp-feat:hover { transform: translateY(-2px); box-shadow: 0 20px 40px -16px rgba(15,23,42,0.1); }
  .pp-feat-icon { width: 40px; height: 40px; border-radius: 10px; background: #e6f0fb; color: #0969da; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 14px; font-size: 18px; }
  .pp-feat-title { font-size: 16px; font-weight: 600; margin: 0 0 6px; }
  .pp-feat-body { font-size: 14px; color: #475569; line-height: 1.6; margin: 0; }

  .pp-stats { background: #f8fafc; padding: 56px 24px; }
  .pp-stats-grid { max-width: 1000px; margin: 0 auto; display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 24px; text-align: center; }
  .pp-stat-value { font-size: 42px; font-weight: 700; letter-spacing: -0.02em; color: #0969da; line-height: 1; margin-bottom: 6px; }
  .pp-stat-label { font-size: 14px; color: #475569; }

  .pp-cta { max-width: 820px; margin: 0 auto; padding: 72px 24px; text-align: center; }
  .pp-cta-h { font-size: 28px; font-weight: 700; letter-spacing: -0.015em; margin: 0 0 10px; }
  .pp-cta-p { font-size: 15px; color: #475569; margin: 0 0 24px; }

  @media (max-width: 820px) {
    .pp-h1 { font-size: 34px; }
    .pp-section-h { font-size: 26px; }
    .pp-feat-grid { grid-template-columns: 1fr; }
    .pp-section, .pp-hero, .pp-stats, .pp-cta { padding-left: 20px; padding-right: 20px; }
  }
`;

export default function ProductPage({ badge, title, titleAccent, subtitle, features, stats, ctaTitle, ctaBody, metaTitle, metaDesc }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <MarketingLayout>
      <Helmet>
        <title>{metaTitle || title + ' — ClickSaver'}</title>
        <meta name="description" content={metaDesc || subtitle} />
      </Helmet>
      <style>{CSS}</style>

      <section className="pp-hero">
        <div className="pp-hero-inner">
          {badge && <div className="pp-badge">{badge}</div>}
          <h1 className="pp-h1">
            {title}
            {titleAccent && <> <span className="accent">{titleAccent}</span></>}
          </h1>
          <p className="pp-sub">{subtitle}</p>
          <div className="pp-cta-row">
            <Link to="/register" className="pp-btn pp-btn-primary">Start free trial</Link>
            <Link to="/pricing" className="pp-btn pp-btn-outline">See pricing</Link>
          </div>
        </div>
      </section>

      <section className="pp-section">
        <h2 className="pp-section-h">What you get</h2>
        <div className="pp-feat-grid">
          {features.map(f => (
            <div key={f.title} className="pp-feat">
              <div className="pp-feat-icon">{f.icon}</div>
              <h3 className="pp-feat-title">{f.title}</h3>
              <p className="pp-feat-body">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {stats && (
        <section className="pp-stats">
          <div className="pp-stats-grid">
            {stats.map(s => (
              <div key={s.label}>
                <div className="pp-stat-value">{s.value}</div>
                <div className="pp-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="pp-cta">
        <h2 className="pp-cta-h">{ctaTitle}</h2>
        <p className="pp-cta-p">{ctaBody}</p>
        <div className="pp-cta-row">
          <Link to="/register" className="pp-btn pp-btn-primary">Start 7-day free trial</Link>
          <Link to="/contact" className="pp-btn pp-btn-outline">Talk to sales</Link>
        </div>
      </div>
    </MarketingLayout>
  );
}
